import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams, Link } from 'react-router-dom';
import { ShoppingBag, ArrowLeft, Check, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useRegion } from '../context/RegionContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/* ─ Styles ─ */
const Page = styled.main`background: #FFFDF7; min-height: 100vh; padding-top: 90px;`;
const Wrap = styled.div`max-width: 1200px; margin: 0 auto; padding: 40px 32px 120px;`;
const Breadcrumb = styled.div`display:flex; align-items:center; gap:8px; font-size:.8rem; color:rgba(26,18,8,.5); margin-bottom:40px;`;
const Crumb = styled(Link)`color:rgba(26,18,8,.5); text-decoration:none; &:hover{color:#7C3A00;}`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 420px;
  gap: 64px;
  align-items: start;
  @media (max-width: 900px) { grid-template-columns: 1fr; gap: 40px; }
`;

/* Photo side */
const PhotoWrap = styled.div`position:relative;`;
const MainImg = styled.div`
  aspect-ratio: 4/3;
  border-radius: 20px;
  overflow: hidden;
  background: ${({$bg})=>$bg||'#F5EFE6'};
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 16px;
`;
const Thumbs = styled.div`display:flex; gap:12px;`;
const Thumb = styled.button`
  width:80px; height:64px; border-radius:10px; overflow:hidden;
  border:2px solid ${({$active})=>$active?'#7C3A00':'rgba(26,18,8,0.1)'};
  cursor:pointer; background:#F5EFE6; padding:0;
  img { width:100%; height:100%; object-fit:cover; }
`;

/* Info side */
const Eyebrow = styled.p`font-family:'Raleway',serif; font-size:.7rem; letter-spacing:.2em; color:#C07020; margin:0 0 10px;`;
const Title = styled.h1`font-family:'Cormorant Garamond',serif; font-size:clamp(1.8rem,3vw,2.6rem); font-weight:400; color:#1A1208; margin:0 0 16px; line-height:1.15;`;
const TypeBadge = styled.span`
  display:inline-block; padding:5px 16px; border-radius:999px;
  background:${({$v})=>$v==='envinada'?'rgba(26,18,8,0.88)':'rgba(255,253,247,0.92)'};
  color:${({$v})=>$v==='envinada'?'#FAF3E0':'#7C3A00'};
  border:1px solid ${({$v})=>$v==='envinada'?'rgba(250,243,224,.18)':'rgba(124,58,0,.2)'};
  font-size:.65rem; letter-spacing:.12em; margin-bottom:20px;
`;
const Price = styled.div`font-family:'Cormorant Garamond',serif; font-size:2.2rem; color:#7C3A00; margin:16px 0 24px;`;
const Desc = styled.p`font-size:.95rem; line-height:1.75; color:rgba(26,18,8,.65); margin:0 0 28px;`;
const Divider = styled.hr`border:none; border-top:1px solid rgba(26,18,8,.07); margin:24px 0;`;

const QtyRow = styled.div`display:flex; align-items:center; gap:16px; margin-bottom:20px;`;
const QtyBtn = styled.button`
  width:36px; height:36px; border-radius:8px; border:1px solid rgba(26,18,8,.12);
  font-size:1.2rem; display:flex; align-items:center; justify-content:center; cursor:pointer;
  &:hover{border-color:#7C3A00;}
`;
const QtyInput = styled.input`
  width:56px; text-align:center; border:1px solid rgba(26,18,8,.12); border-radius:8px;
  padding:8px 4px; font-size:1rem; color:#1A1208;
`;

const AddBtn = styled.button`
  display:flex; align-items:center; justify-content:center; gap:10px;
  width:100%; padding:16px; border-radius:12px; background:#7C3A00; color:#FFFDF7;
  font-size:.9rem; letter-spacing:.08em; font-weight:600;
  cursor:pointer; transition:background .2s, transform .2s;
  &:hover{background:#1A1208; transform:translateY(-2px);}
`;

const Features = styled.ul`list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:10px;`;
const Feat = styled.li`display:flex; align-items:center; gap:10px; font-size:.88rem; color:rgba(26,18,8,.7);`;

const pop = keyframes`from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:translateY(0);}`;
const AddedNote = styled.div`
  display:flex; align-items:center; gap:8px; color:#3a7c3a; font-size:.88rem;
  margin-top:12px; animation:${pop} .35s ease;
`;

const Related = styled.section`margin-top:80px;`;
const RelGrid = styled.div`display:grid; grid-template-columns:repeat(3,1fr); gap:20px; @media(max-width:640px){grid-template-columns:1fr 1fr;}`;
const RelCard = styled(Link)`
  text-decoration:none; display:flex; flex-direction:column; gap:8px;
  &:hover div:first-child img{transform:scale(1.05);}
`;
const RelImg = styled.div`
  aspect-ratio:1; border-radius:12px; overflow:hidden; background:#F5EFE6;
  display:flex; align-items:center; justify-content:center;
  img{width:90%;height:90%;object-fit:contain;transition:transform .35s;}
`;

export default function ProductPage() {
  const { id } = useParams();
  const { lang } = useLanguage();
  const { addToCart } = useCart();
  const { region, symbol: currSym, currency } = useRegion();
  const isMxn   = region === 'mx';
  const currCode = isMxn ? 'MXN' : 'USD';
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setLoadingProduct(true);
    // Carga el producto específico
    fetch(`${API}/api/products/${id}`)
      .then(r => { if (!r.ok) throw new Error('not found'); return r.json(); })
      .then(data => {
        setProduct(data);
        setLoadingProduct(false);
        // Carga productos relacionados (mismo slug/variedad)
        fetch(`${API}/api/products`)
          .then(r => r.json())
          .then(all => setRelated(all.filter(p => p.id !== data.id && p.v === data.v).slice(0, 3)))
          .catch(() => {});
      })
      .catch(() => { setProduct(null); setLoadingProduct(false); });
  }, [id]);

  if (loadingProduct) return (
    <Page><Wrap>
      <p style={{color:'rgba(26,18,8,.5)'}}>{lang==='es' ? 'Cargando\u2026' : 'Loading\u2026'}</p>
    </Wrap></Page>
  );

  if (!product) return (
    <Page><Wrap>
      <p>{lang==='es' ? 'Producto no encontrado.' : 'Product not found.'} <Link to={`/${region}/store`}>{lang==='es'?'Volver a tienda':'Back to store'}</Link></p>
    </Wrap></Page>
  );

  const item = product[lang] || product.en;

  function handleAdd() {
    addToCart(product.id, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2200);
  }

  const images = product.image ? [product.image, product.image] : ['/images/hero-cajeta.jpg', '/images/doscajetasunataza.jpeg'];

  const features = lang === 'es'
    ? ['100% leche de vaca', 'Sin conservadores', 'Receta original desde 1950', 'Frasco de vidrio 700 g']
    : ['100% cow\'s milk', 'No preservatives', 'Original recipe since 1950', '700 g glass jar'];

  return (
    <Page>
      <Wrap>
        <Breadcrumb>
          <Crumb to={`/${region}/store`}>{lang==='es'?'Tienda':'Store'}</Crumb>
          <ChevronRight size={14} />
          <span style={{color:'#1A1208'}}>{item.name}</span>
        </Breadcrumb>

        <Layout>
          {/* Photo side */}
          <PhotoWrap>
            <MainImg $bg={product.bg||'#F5EFE6'}>
              <img src={images[activeImg]} alt={item.name} style={{width:'92%',height:'92%',objectFit:'contain'}} />
            </MainImg>
            <Thumbs>
              {images.map((src, i) => (
                <Thumb key={i} $active={activeImg===i} onClick={()=>setActiveImg(i)}>
                  <img src={src} alt="" />
                </Thumb>
              ))}
            </Thumbs>
          </PhotoWrap>

          {/* Info side */}
          <aside>
            <Eyebrow>HACIENDA REAL · CAJETA</Eyebrow>
            <TypeBadge $v={product.v}>{product.v === 'envinada' ? (lang==='es'?'Envinada':'Envinada') : (lang==='es'?'Natural':'Natural')}</TypeBadge>
            <Title>{item.name}</Title>
            <Desc>{item.desc}</Desc>

            <Features>
              {features.map(f => (
                <Feat key={f}><Check size={14} color="#C07020" />{f}</Feat>
              ))}
            </Features>

            <Divider />

            <Price>
              {(() => {
                const p = isMxn ? (product.priceMxn || product.price) : product.price;
                return p > 0 ? `${currSym}${p.toFixed(2)}` : (lang==='es'?'Precio a consultar':'Price on request');
              })()}
            </Price>

            <QtyRow>
              <QtyBtn onClick={()=>setQty(q=>Math.max(1,q-1))}>−</QtyBtn>
              <QtyInput type="number" min={1} value={qty} onChange={e=>setQty(Math.max(1,Number(e.target.value)))} />
              <QtyBtn onClick={()=>setQty(q=>q+1)}>+</QtyBtn>
              <span style={{fontSize:'.85rem',color:'rgba(26,18,8,.45)'}}>
                {(() => {
                  const p = isMxn ? (product.priceMxn || product.price) : product.price;
                  return p > 0 ? `Total: ${currSym}${(p*qty).toFixed(2)}` : '';
                })()}
              </span>
            </QtyRow>

            <AddBtn onClick={handleAdd}>
              <ShoppingBag size={18} />
              {lang==='es'?'Agregar al carrito':'Add to cart'}
            </AddBtn>

            {added && (
              <AddedNote><Check size={16}/>{lang==='es'?'¡Añadido al carrito!':'Added to cart!'}</AddedNote>
            )}
          </aside>
        </Layout>

        {related.length > 0 && (
          <Related>
            <p style={{fontFamily:'Raleway,serif',color:'#C07020',letterSpacing:'.18em',fontSize:'.72rem',marginBottom:24}}>
              {lang==='es'?'TAMBIÉN TE PUEDE GUSTAR':'YOU MAY ALSO LIKE'}
            </p>
            <RelGrid>
              {related.map(r=>{
                const ri = r[lang]||r.en;
                return (
                  <RelCard to={`/product/${r.id}`} key={r.id}>
                    <RelImg>
                      {r.image ? <img src={r.image} alt={ri.name}/> : <div style={{fontSize:40}}>🫙</div>}
                    </RelImg>
                    <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#1A1208'}}>{ri.name}</div>
                    <div style={{color:'#7C3A00',fontWeight:700}}>{(() => { const p = isMxn?(r.priceMxn||r.price):r.price; return p>0?`${currSym}${p.toFixed(2)}`:''; })()}</div>
                  </RelCard>
                );
              })}
            </RelGrid>
          </Related>
        )}
      </Wrap>
    </Page>
  );
}
